import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Typography,
  Fade,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import useAuthStore from '../stores/authStore';
import './Onboarding.css';

function Onboarding() {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    fullName: user?.full_name || user?.fullName || '',
    birthday: user?.birthday || '',
    linkedinLink: user?.linkedin_link || user?.linkedinLink || '',
    preferredLevel: user?.preferred_level || user?.preferredLevel || '',
    whyJoin: user?.why_join || user?.whyJoin || '',
  });

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/signin');
      return;
    }

    if (user?.accountType === 'employer') {
      navigate('/post-job');
    }
  }, [isAuthenticated, navigate, user?.accountType]);

  if (!isAuthenticated()) {
    return null;
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.fullName.trim()) {
      setError('Full name is required');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/onboarding`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${useAuthStore.getState().token}`,
        },
        body: JSON.stringify({
          fullName: formData.fullName,
          birthday: formData.birthday,
          linkedinLink: formData.linkedinLink,
          preferredLevel: formData.preferredLevel,
          whyJoin: formData.whyJoin,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to save onboarding information');
      }

      const data = await response.json();

      if (data.user) {
        useAuthStore.setState({
          user: {
            ...useAuthStore.getState().user,
            ...data.user,
            full_name: data.user.fullName,
            avatar_url: data.user.avatarUrl,
            linkedin_link: data.user.linkedinLink,
            why_join: data.user.whyJoin,
            preferred_level: data.user.preferredLevel,
          },
        });
      }

      navigate('/');
    } catch (err) {
      setError(err.message || 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box className="onboarding ch-page-enter">
      <Box className="onboarding-container">
        <Fade in timeout={220}>
          <Paper className="onboarding-card ch-card-enter" elevation={0}>
            <Box className="onboarding-card-accent" aria-hidden="true" />

            <Stack spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
              <Chip label="Candidate profile" size="small" color="primary" variant="outlined" />
              <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem' }, m: 0 }}>
                Welcome to CofounderHubs
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ m: 0 }}>
                Let&apos;s get to know you better
              </Typography>
            </Stack>

            <Box sx={{ mb: 1.75 }}>
              <LinearProgress variant="determinate" value={35} sx={{ mb: 0.75 }} />
              <Typography variant="caption" color="text.secondary">
                Complete your profile to start connecting
              </Typography>
            </Box>

            <Box component="form" onSubmit={handleSubmit}>
              <Stack spacing={1.25}>
                {error && (
                  <Alert severity="error" sx={{ py: 0.5 }}>
                    {error}
                  </Alert>
                )}

                <Typography
                  variant="overline"
                  sx={{ color: 'text.secondary', letterSpacing: '0.08em', lineHeight: 1.2 }}
                >
                  Personal details
                </Typography>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                  <TextField
                    label="Full name"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleChange}
                    placeholder="Enter your full name"
                    required
                  />
                  <DatePicker
                    label="Birthday"
                    value={formData.birthday ? dayjs(formData.birthday) : null}
                    onChange={(value) => {
                      setFormData((prev) => ({
                        ...prev,
                        birthday: value && value.isValid() ? value.format('YYYY-MM-DD') : '',
                      }));
                      setError('');
                    }}
                    slotProps={{
                      textField: {
                        size: 'small',
                        fullWidth: true,
                      },
                    }}
                  />
                </Stack>

                <Typography
                  variant="overline"
                  sx={{ color: 'text.secondary', letterSpacing: '0.08em', lineHeight: 1.2, pt: 0.5 }}
                >
                  Professional details
                </Typography>

                <TextField
                  label="LinkedIn URL"
                  name="linkedinLink"
                  type="url"
                  value={formData.linkedinLink}
                  onChange={handleChange}
                  placeholder="https://linkedin.com/in/yourprofile"
                />

                <TextField
                  label="Preferred level"
                  name="preferredLevel"
                  type="number"
                  value={formData.preferredLevel}
                  onChange={handleChange}
                  placeholder="Enter level"
                  inputProps={{ min: 1, max: 100000 }}
                  helperText="Max 100,000"
                  FormHelperTextProps={{ sx: { m: 0, mt: 0.35 } }}
                />

                <TextField
                  label="Why did you join CofounderHubs?"
                  name="whyJoin"
                  value={formData.whyJoin}
                  onChange={handleChange}
                  placeholder="Tell us why you joined..."
                  multiline
                  minRows={3}
                />

                <Typography variant="caption" color="text.secondary" sx={{ bgcolor: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 1, px: 1.25, py: 1 }}>
                  You can upload a profile photo and update these details later from your Profile page.
                </Typography>

                <Button type="submit" variant="contained" color="primary" disabled={loading} fullWidth sx={{ mt: 0.5 }}>
                  {loading ? 'Saving...' : 'Continue'}
                </Button>
              </Stack>
            </Box>
          </Paper>
        </Fade>
      </Box>
    </Box>
  );
}

export default Onboarding;
